import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Edit, Search, Save, Loader2, X, CheckCircle, AlertCircle, Upload, Image as ImageIcon, Plus } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Regions a destination can be affiliated with (matches AllDestinations REGION_CONFIG).
const REGION_OPTIONS = ['Global', 'Asia', 'Europe', 'North America', 'South America', 'Africa', 'Oceania', 'Caribbean', 'Middle East', 'Other'];

/**
 * Admin panel to override the display NAME and COVER IMAGE of any destination.
 * Names + covers load into AppContext on app mount and are applied everywhere
 * (web + native mobile via the public override endpoints). Supports per-row
 * save and a "Save All" action for bulk edits.
 */
export const CountryNamesPanel = () => {
  const { allCountries, nameOverrides, refreshNameOverrides, regionOverrides, refreshRegionOverrides, destinationTags, refreshDestinationTags } = useApp();
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({}); // {code: pendingName}
  const [covers, setCovers] = useState({}); // {code: currentImageUrl} from server
  const [coverDrafts, setCoverDrafts] = useState({}); // {code: pendingImageUrl}
  const [regionDrafts, setRegionDrafts] = useState({}); // {code: pendingRegion}
  const [tagDrafts, setTagDrafts] = useState({}); // {code: [pendingTags]}
  const [tagInputs, setTagInputs] = useState({}); // {code: pending "add tag" text}
  const [savingCode, setSavingCode] = useState(null);
  const [uploadingCode, setUploadingCode] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [flash, setFlash] = useState(null);

  const refreshCovers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cover-image-overrides`);
      const map = {};
      (res.data?.overrides || []).forEach((o) => { if (o.code) map[o.code] = o.image_url; });
      setCovers(map);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { refreshCovers(); }, [refreshCovers]);

  // Auto-dismiss the flash confirmation after a few seconds.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = allCountries.map((c) => ({
      code: c.id,
      currentName: c.name,
      override: nameOverrides[c.id] || '',
      cover: covers[c.id] || '',
      region: regionOverrides[c.id] || '',
      tags: destinationTags[c.id] || [],
      isRegion: c.isRegion,
    }));
    if (!q) return list;
    return list.filter(
      (r) => r.code.toLowerCase().includes(q) || r.currentName.toLowerCase().includes(q) || (r.override || '').toLowerCase().includes(q)
    );
  }, [allCountries, nameOverrides, covers, regionOverrides, destinationTags, search]);

  const setDraft = (code, value) => setDrafts((p) => ({ ...p, [code]: value }));
  const setCoverDraft = (code, value) => setCoverDrafts((p) => ({ ...p, [code]: value }));
  const setRegionDraft = (code, value) => setRegionDrafts((p) => ({ ...p, [code]: value }));

  const saveName = async (code, overrideName) => {
    await axios.post(`${API}/admin/country-name-overrides`, { code, override_name: overrideName || '' });
  };
  const saveCover = async (code, imageUrl) => {
    await axios.post(`${API}/admin/cover-image-overrides`, { code, image_url: imageUrl || '' });
  };
  const saveRegion = async (code, region) => {
    await axios.post(`${API}/admin/region-overrides`, { code, region: region || '' });
  };
  const saveTags = async (code, tags) => {
    await axios.post(`${API}/admin/destination-tags`, { code, tags: tags || [] });
  };

  const tagsEqual = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
  const rowDirty = (r) =>
    (drafts[r.code] !== undefined && drafts[r.code] !== r.override) ||
    (coverDrafts[r.code] !== undefined && coverDrafts[r.code] !== r.cover) ||
    (regionDrafts[r.code] !== undefined && regionDrafts[r.code] !== r.region) ||
    (tagDrafts[r.code] !== undefined && !tagsEqual(tagDrafts[r.code], r.tags));

  const addTag = (r) => {
    const raw = (tagInputs[r.code] || '').trim();
    if (!raw) return;
    const current = tagDrafts[r.code] !== undefined ? tagDrafts[r.code] : r.tags;
    if (current.some((t) => t.toLowerCase() === raw.toLowerCase()) || current.length >= 6) {
      setTagInputs((p) => ({ ...p, [r.code]: '' }));
      return;
    }
    setTagDrafts((p) => ({ ...p, [r.code]: [...current, raw.slice(0, 24)] }));
    setTagInputs((p) => ({ ...p, [r.code]: '' }));
  };
  const removeTag = (r, tag) => {
    const current = tagDrafts[r.code] !== undefined ? tagDrafts[r.code] : r.tags;
    setTagDrafts((p) => ({ ...p, [r.code]: current.filter((t) => t !== tag) }));
  };

  const clearRowDrafts = (code) => {
    setDrafts((p) => { const n = { ...p }; delete n[code]; return n; });
    setCoverDrafts((p) => { const n = { ...p }; delete n[code]; return n; });
    setRegionDrafts((p) => { const n = { ...p }; delete n[code]; return n; });
    setTagDrafts((p) => { const n = { ...p }; delete n[code]; return n; });
  };

  const persistRow = async (r) => {
    setSavingCode(r.code);
    setFlash(null);
    try {
      if (drafts[r.code] !== undefined && drafts[r.code] !== r.override) await saveName(r.code, (drafts[r.code] || '').trim());
      if (coverDrafts[r.code] !== undefined && coverDrafts[r.code] !== r.cover) await saveCover(r.code, (coverDrafts[r.code] || '').trim());
      if (regionDrafts[r.code] !== undefined && regionDrafts[r.code] !== r.region) await saveRegion(r.code, regionDrafts[r.code]);
      if (tagDrafts[r.code] !== undefined && !tagsEqual(tagDrafts[r.code], r.tags)) await saveTags(r.code, tagDrafts[r.code]);
      await Promise.all([refreshNameOverrides(), refreshCovers(), refreshRegionOverrides(), refreshDestinationTags()]);
      clearRowDrafts(r.code);
      setFlash({ type: 'ok', msg: `Saved ${r.code}` });
    } catch (e) {
      setFlash({ type: 'err', msg: e.response?.data?.detail || e.message || 'Save failed' });
    } finally {
      setSavingCode(null);
    }
  };

  const clearRow = async (r) => {
    setSavingCode(r.code);
    setFlash(null);
    try {
      await Promise.all([saveName(r.code, ''), saveCover(r.code, ''), saveRegion(r.code, ''), saveTags(r.code, [])]);
      await Promise.all([refreshNameOverrides(), refreshCovers(), refreshRegionOverrides(), refreshDestinationTags()]);
      clearRowDrafts(r.code);
      setFlash({ type: 'ok', msg: `Cleared ${r.code}` });
    } catch (e) {
      setFlash({ type: 'err', msg: e.response?.data?.detail || 'Clear failed' });
    } finally {
      setSavingCode(null);
    }
  };

  const handleUpload = async (code, file) => {
    if (!file) return;
    setUploadingCode(code);
    setFlash(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.url) setCoverDraft(code, res.data.url);
    } catch (e) {
      setFlash({ type: 'err', msg: e.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploadingCode(null);
    }
  };

  const dirtyCount = useMemo(() => {
    let n = 0;
    rows.forEach((r) => { if (rowDirty(r)) n++; });
    return n;
  }, [rows, drafts, coverDrafts, regionDrafts, tagDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAll = async () => {
    setSavingAll(true);
    setFlash(null);
    let ok = 0, fail = 0;
    for (const r of rows) {
      if (!rowDirty(r)) continue;
      try {
        if (drafts[r.code] !== undefined && drafts[r.code] !== r.override) await saveName(r.code, (drafts[r.code] || '').trim());
        if (coverDrafts[r.code] !== undefined && coverDrafts[r.code] !== r.cover) await saveCover(r.code, (coverDrafts[r.code] || '').trim());
        if (regionDrafts[r.code] !== undefined && regionDrafts[r.code] !== r.region) await saveRegion(r.code, regionDrafts[r.code]);
        if (tagDrafts[r.code] !== undefined && !tagsEqual(tagDrafts[r.code], r.tags)) await saveTags(r.code, tagDrafts[r.code]);
        ok++;
      } catch (_) { fail++; }
    }
    await Promise.all([refreshNameOverrides(), refreshCovers(), refreshRegionOverrides(), refreshDestinationTags()]);
    setDrafts({});
    setCoverDrafts({});
    setRegionDrafts({});
    setTagDrafts({});
    setSavingAll(false);
    setFlash({ type: fail ? 'err' : 'ok', msg: `Saved ${ok} row${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}` });
  };

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-3xl border border-gray-200 dark:border-neutral-800 overflow-hidden" data-testid="country-names-panel">
      <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 flex items-center gap-2">
              <Edit size={20} className="text-emerald-600" />
              Destination Names & Covers
            </h2>
            <p className="text-gray-500 dark:text-neutral-400 text-sm mt-1">
              Override the display name and cover image for any destination. Blank + save clears an override. Changes apply on web and mobile.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative min-w-[220px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code or name…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                data-testid="country-names-search"
              />
            </div>
            <button
              type="button" onClick={saveAll} disabled={savingAll || dirtyCount === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 dark:border dark:border-white whitespace-nowrap"
              data-testid="country-names-save-all"
            >
              {savingAll ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Save All{dirtyCount ? ` (${dirtyCount})` : ''}
            </button>
          </div>
        </div>

        {flash && (
          <div className={`mt-4 flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${flash.type === 'ok' ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10' : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-500/10'}`} data-testid="country-names-flash">
            {flash.type === 'ok' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <span>{flash.msg}</span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-neutral-950/50 text-gray-600 dark:text-neutral-400">
            <tr>
              <th className="text-left px-4 py-3 font-semibold w-28">Code</th>
              <th className="text-left px-4 py-3 font-semibold">Name override</th>
              <th className="text-left px-4 py-3 font-semibold w-40">Region</th>
              <th className="text-left px-4 py-3 font-semibold">Tags</th>
              <th className="text-left px-4 py-3 font-semibold">Cover image</th>
              <th className="text-left px-4 py-3 font-semibold w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-neutral-500">No destinations match your search.</td></tr>
            )}
            {rows.map((r) => {
              const nameVal = drafts[r.code] !== undefined ? drafts[r.code] : r.override;
              const coverVal = coverDrafts[r.code] !== undefined ? coverDrafts[r.code] : r.cover;
              const regionVal = regionDrafts[r.code] !== undefined ? regionDrafts[r.code] : r.region;
              const tagsVal = tagDrafts[r.code] !== undefined ? tagDrafts[r.code] : r.tags;
              const dirty = rowDirty(r);
              const isSaving = savingCode === r.code;
              return (
                <tr key={r.code} className="hover:bg-gray-50 dark:hover:bg-neutral-800/50" data-testid={`country-name-row-${r.code}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-neutral-300 align-top">
                    {r.code}
                    {r.isRegion && <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 text-[10px] uppercase">Region</span>}
                    <div className="text-gray-400 mt-1 font-sans">{r.currentName}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <input
                      type="text" value={nameVal} onChange={(e) => setDraft(r.code, e.target.value)} placeholder="(no override)"
                      className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                      data-testid={`country-name-override-${r.code}`}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <select
                      value={regionVal}
                      onChange={(e) => setRegionDraft(r.code, e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                      data-testid={`country-region-${r.code}`}
                    >
                      <option value="">(auto)</option>
                      {REGION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      {tagsVal.length === 0 ? (
                        <span className="text-xs text-gray-400 dark:text-neutral-600">(no tags)</span>
                      ) : tagsVal.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-semibold" data-testid={`country-tag-${r.code}-${t}`}>
                          {t}
                          <button type="button" onClick={() => removeTag(r, t)} className="hover:text-red-600" data-testid={`country-tag-remove-${r.code}-${t}`}>
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={tagInputs[r.code] || ''}
                        onChange={(e) => setTagInputs((p) => ({ ...p, [r.code]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(r); } }}
                        placeholder="Add tag"
                        maxLength={24}
                        className="w-28 min-w-0 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-xs focus:outline-none focus:border-black dark:focus:border-white"
                        data-testid={`country-tag-input-${r.code}`}
                      />
                      <button type="button" onClick={() => addTag(r)} className="inline-flex items-center px-2 py-1 rounded-lg border border-gray-200 dark:border-neutral-700 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-neutral-800" data-testid={`country-tag-add-${r.code}`}>
                        <Plus size={13} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      {coverVal ? (
                        <img src={coverVal} alt="" className="w-12 h-9 rounded object-cover border border-gray-200 dark:border-neutral-700" />
                      ) : (
                        <div className="w-12 h-9 rounded bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-400"><ImageIcon size={14} /></div>
                      )}
                      <input
                        type="text" value={coverVal} onChange={(e) => setCoverDraft(r.code, e.target.value)} placeholder="Image URL (or upload →)"
                        className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                        data-testid={`country-cover-url-${r.code}`}
                      />
                      <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 text-xs font-semibold cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-800 whitespace-nowrap" data-testid={`country-cover-upload-${r.code}`}>
                        {uploadingCode === r.code ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        Upload
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(r.code, e.target.files?.[0])} />
                      </label>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => persistRow(r)}
                        disabled={!dirty || isSaving}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-40 dark:border dark:border-white"
                        data-testid={`country-name-save-${r.code}`}
                      >
                        {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        Save
                      </button>
                      {(r.override || r.cover || r.region || (r.tags && r.tags.length)) && !dirty && (
                        <button
                          type="button"
                          onClick={() => clearRow(r)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-red-600 dark:text-red-400 text-xs font-semibold hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40"
                          data-testid={`country-name-clear-${r.code}`}
                          title="Remove all overrides"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CountryNamesPanel;
